import Action from "./class.Action";
import {Parsers} from "../class.TransporterParser.js";
import * as Input from "../class.TransporterInput";
import * as ActionUtils from "./class.ActionUtils";

/**
 * A stub class for parseable actions.
 *
 * Requirements:
 * 	The z-data object modeling this action must contain the follow fields:
 * 		options
 * 		trigger
 * 		body (array:prim)
 * 		hints (optional)
 * 		give-up-trigger (optional)
 */
export default class ActionParseable extends Action {
	constructor(options) {
		super(options);

		// A reference to inputs which have been created.
		this._inputs = [];

		// A reference to the bubble containing the problem.
		this.problemView = null;
		// A reference to the bubble containing the student response.
		this.responseView = null;
		// The student's last response.
		this.response = null;
		// The preview of the student's last response.
		this.preview = null;
		// Keep track of submit button for enable/disabling.
		this.submitButton = null;
		this.submittable = true;

		// Whether the student gave up.
		this.gaveUp = false;
		// The attempt number we're on.
		this.attempt = 0;
		// Whether the student passed the question.
		this.passed = false;
		// Whether this model has hints.
		this.hasHints = this.model.hints && this.model.hints.length;
		// Index of the next hint.
		this.nextHint = 0;
		this.lastHint = null;
		// Is this parsed or unparsed?
		this.isParsed =
			this.model.options &&
			this.model.options.length !== 0 &&
			!this.model.type.match("unparsed");

		// Track repeat submissions
		this.submissions = [];

		// Keep track of whether parsing calls failed.
		this.parsingError = false;

		this.initializeAnalytics();
	}

	async render($anchor) {
		if (this.taItem) {
			this.taTrial = this.taItem.createTrial({
				attempt: this.attempt,
				last_hint: this.lastHint,
			});
		}
		const $problemDiv = await this.renderProblemBubble($anchor);
		this.problemView.$el.hide();
		await this.onRenderProblem($problemDiv);

		await this.animationController.FadeIn.start({
			bubbleView: this.problemView,
		}).scroll();

		await this.postRenderProblem($problemDiv);
	}

	/**
	 * Define your problem rendering logic here.
	 * @param {*} $problemDiv A div to render your problem contents in.
	 */
	onRenderProblem($problemDiv) {
		return new Promise((resolve) => {
			$problemDiv.html(`<span>:)</span>`);
			resolve();
		});
	}

	/**
	 * Define your post-rendering logic here (after the bubble is visible).
	 * @param {*} $problemDiv A div containing your problem contents.
	 */
	postRenderProblem($problemDiv) {
		return new Promise((resolve) => {
			resolve();
		});
	}

	/**
	 * Define your student response rendering logic here.
	 * This determines the behavior BEFORE the student submits (while the response is being inputted).
	 * @param {*} $responseDiv A div to render your response contents in.
	 * @param {*} response
	 * @param {*} preview
	 */
	onRenderResponse($responseDiv, response, preview) {
		return new Promise((resolve) => {
			$responseDiv.text(response);
			resolve();
		});
	}

	/**
	 * Define your give up logic here.
	 * @param {*} $giveUpDiv A div to render your give up message in.
	 */
	onRenderGiveUp($giveUpDiv) {
		return new Promise((resolve) => {
			$giveUpDiv.html("I give up.");
			resolve();
		});
	}

	/**
	 * Define your submit logic here. This determines the behavior AFTER the student has submitted.
	 * By default, submitDiv's initial state is the same as responseDiv's final state after onRenderResponse.
	 * @param {*} $submitDiv A div to render your submit message in.
	 */
	onRenderSubmit($submitDiv, response, preview) {
		return new Promise((resolve) => {
			resolve();
		});
	}

	/**
	 * Define your extra submit logic here. (e.g. turning off Motors)
	 */
	onSubmit() {
		return new Promise((resolve) => {
			resolve();
		});
	}

	/**
	 * Define your parsing logic here.
	 * @param {*} response The response data.
	 * @returns {Promise<number>}
	 * 	A promise resolving with the index of the matching option, or -1.
	 */
	onParseResponse(response) {
		return this.parseResponse(response.toString());
	}

	/**
	 * Returns promise which resolves with the index of an option
	 * which matches the student's response.
	 *
	 * Resolves -1 if the answer was incorrect.
	 *
	 * @param response The student's response.
	 * @returns A promise resolving with the index of the matching option, or -1.
	 */
	parseResponse(response) {
		return new Promise(async (resolve, reject) => {
			const options = this.model.options;
			let index = -1;
			if (options.length === 0) {
				resolve(index);
			}
			try {
				index = await Parsers.auto.indexOfResponse(options, response);
				if (this.mainController.isPreviewMode) {
					this.showParserMatch(response, index);
				}
				resolve(index);
			} catch (e) {
				reject(e);
			}
		});
	}

	/**
	 * Displays the matched option in a modal
	 * @param {string} response the student's response
	 * @param {number} index the index of the matched option
	 */
	showParserMatch(response, index) {
		let messageHTML = `<p><strong>Response:</strong> ${response}</p>`;
		if (index === -1) {
			messageHTML += "<p>No options matched</p>";
		} else {
			const option = this.model.options[index];
			messageHTML += `
				<p><strong>Matched option:</strong> ${option.match.join(" or ")}</p>
				<p><strong>Index of matched option:</strong> ${index}</p>
				<p style="color:${option.is_correct ? "green" : "red"}">
					<strong>${option.is_correct ? "Correct" : "Incorrect"}</strong>
				</p>
			`;
		}
		$(messageHTML).showModal();
	}

	/**
	 * Traverse the flow after this problem has been passed.
	 * @param {*} $problemAnchor
	 */
	passProblem($problemAnchor) {
		this.passed = true;
		let trigger = "trigger";
		const hasGiveUpTrigger =
			this.model["give-up-trigger"] && this.model["give-up-trigger"].length;
		if (this.gaveUp) {
			if (this.taTrial) {
				this.taTrial.setGaveUp();
			}
			if (hasGiveUpTrigger) {
				trigger = "give-up-trigger";
			}
		} else {
			if (this.taTrial) {
				this.taTrial.setPassed();
			}
		}
		this.runnerController.createAndTraverseFlow({
			pointer: this.pointer,
			item: this.model,
			flow: [{traverse: trigger}],
			onAdvance: ($newAnchor) => {
				this.onAdvance($newAnchor);
			},
			onBypass: () => {
				this.onBypass();
			},
			$anchor: $problemAnchor,
			context: this.context,
		});
	}

	/**
	 * Renders the problem prompt and returns a div where you can render your problem.
	 *
	 * @param {*} $anchor
	 * @returns {*} $problemDiv A div where you can render the contents of your problem.
	 */
	async renderProblemBubble($anchor) {
		if (!$anchor) {
			$anchor = this.$anchor;
		}

		if (this.model.center) {
			// Centered problem view.
			this.problemView = this.layoutView.placeBubble(
				$anchor,
				$anchor.is(this.layoutView.$el) ? "append" : "after",
				"center"
			);

			for (let prim of this.model.body) {
				if (prim.html || prim.text) {
					const virtualBubble = this.problemView.appendVirtualBubble();
					virtualBubble
						.appendContent($(`<div/>`).html(prim.html || prim.text))
						.setTextAlign(this.model.text_align || "center");
				}
			}
		} else {
			// Uncentered problem view.
			// We must render our prompt, before setting up our problem bubble.
			let lastPrim;
			for (let prim of this.model.body) {
				if (prim.html || prim.text) {
					let anchor;
					let method;
					if ($anchor.is(this.layoutView)) {
						method = "append";
						anchor = this.layoutView.$el;
					} else {
						method = "after";
						if (lastPrim) {
							anchor = lastPrim.$el;
						} else {
							anchor = $anchor;
						}
					}
					lastPrim = this.layoutView.placeBubble(anchor, method, "teacher");
					lastPrim
						.appendContent($("<div />").html(prim.html || prim.text))
						.setTextAlign(this.model.text_align || "center");
					/* eslint-disable-next-line no-await-in-loop */
					await this.animationController.FadeIn.start({
						bubbleView: lastPrim,
					}).scroll();
				}
			}
			let $newAnchor = lastPrim ? lastPrim.$el : $anchor;
			this.problemView = this.layoutView.placeBubble(
				$newAnchor,
				$newAnchor.is(this.layoutView.$el) ? "append" : "after",
				"whiteboard"
			);
		}

		const $problemDiv = $(`<div class="transporter-problem"/>`);
		this.problemView.appendContent($problemDiv);

		return $problemDiv;
	}

	async renderResponseBubble(response = this.response, preview = this.preview) {
		this.response = response;
		this.preview = preview;

		if (!this.responseView) {
			// Create the new bubble.
			this.responseView = this.layoutView.placeBubble(
				this.problemView.$el,
				"after",
				"student"
			);
			const $submitDiv = $(`<div class="btn-submit-container"/>`);
			const $responseDiv = $(`<div class="transporter-response"/>`);
			this.responseView.setContent($responseDiv);
			this.responseView.appendContent($submitDiv);
			this.responseView.$el.addClass("short-answer").hide();
			$responseDiv.css({"max-width": "320px"});
			await this.onRenderResponse($responseDiv, this.response, this.preview);

			$submitDiv.addClass("has-hint");
			if (this.hasHints && this.model.hints.length > this.nextHint) {
				// Create the Hint button.
				const hintPrompt = this.model.hints[this.nextHint].prompt;
				const hintButton = new Input.Button({
					attributes: {
						inputController: this.inputController,
						className: "btn-like",
						tagName: "a",
						target:
							this.pointerStr +
							"Attempt" +
							this.attempt +
							"Hint" +
							this.nextHint,
					},
				});
				this._inputs.push(hintButton);
				hintButton.$el.html(
					$("<div class='inner-content' />").html(
						(hintPrompt && hintPrompt.html) || "I need a hint!"
					)
				);
				hintButton.$el.addClass("color1");
				$submitDiv.append(hintButton.$el);

				// Bind the hint button's action.
				hintButton.onClick({
					once: true,
					callback: async () => {
						if (this.taItem) {
							this.taItem.setInteracted();
						}
						if (this.taTrial) {
							this.taTrial.setRequestedHint();
						}
						this.attempt++;
						this.lastHint = this.nextHint;

						// Deactivate buttons
						while (this._inputs.length > 0) {
							this._inputs.pop().off();
						}

						await this.onSubmit();

						this.responseView.setContent(
							(hintPrompt && hintPrompt.html) || "I need a hint!"
						);
						this.responseView.$el.removeClass("short-answer");
						await this.animationController.FadeIn.start({
							bubbleView: this.responseView,
						});

						// Run any actions in the "trigger" section for this button.
						this.runnerController.createAndTraverseFlow({
							pointer: this.pointer.concat(["hints", this.nextHint]),
							item: this.model.hints[this.nextHint],
							flow: [{traverse: "trigger"}],
							onAdvance: ($newAnchor) => {
								// Re-render the response bubble.
								this.nextHint++;
								this.responseView = null;
								this.render($newAnchor);
							},
							onBypass: () => {
								this.onBypass();
							},
							$anchor: this.responseView.$el,
							context: this.context,
						});
					},
				});
			} else {
				// Create the Give Up button.
				const surrenderButton = new Input.Button({
					attributes: {
						inputController: this.inputController,
						className: "btn-like",
						tagName: "a",
						target: this.pointerStr + "Attempt" + this.attempt + "Surrender",
					},
				});
				this._inputs.push(surrenderButton);
				surrenderButton.$el.html("Give Up");
				surrenderButton.$el.addClass("color3");
				$submitDiv.append(surrenderButton.$el);

				// Bind the give up button's action
				surrenderButton.onClick({
					once: true,
					callback: async () => {
						if (this.taItem) {
							this.taItem.setInteracted();
						}
						if (this.taTrial) {
							this.taTrial.setGaveUp();
						}
						this.attempt++;

						// Deactivate buttons
						while (this._inputs.length > 0) {
							this._inputs.pop().off();
						}

						await this.onSubmit();

						this.responseView.$el.removeClass("short-answer");
						const $giveUpDiv = $(`<div class="transporter-giveup"/>`);
						this.responseView.setContent($giveUpDiv);
						await this.onRenderGiveUp($giveUpDiv);
						await this.animationController.FadeIn.start({
							bubbleView: this.responseView,
						}).scroll();

						this.gaveUp = true;
						this.passProblem(this.responseView.$el);
					},
				});
			}

			// Create the submit button
			const submitButton = new Input.Button({
				attributes: {
					inputController: this.inputController,
					className: "btn-like",
					tagName: "a",
					target: this.pointerStr + "Attempt" + this.attempt + "Submit",
				},
			});
			this._inputs.push(submitButton);
			submitButton.$el.html("Submit");
			submitButton.$el.addClass("color2");
			$submitDiv.append(submitButton.$el);

			// Bind the submit button
			submitButton.onClick({
				once: true,
				callback: async () => {
					if (this.taItem) {
						this.taItem.setInteracted();
					}
					this.attempt++;
					// Deactivate buttons
					while (this._inputs.length > 0) {
						this._inputs.pop().off();
					}
					this.submitButton = null;

					await this.onSubmit();
					await this.onRenderSubmit($responseDiv, this.response, this.preview);

					if (this.taTrial) {
						this.taTrial.setResponse(JSON.stringify(this.response));
					}

					// Place the student response bubble.
					$submitDiv.remove();
					this.responseView.$el.removeClass("short-answer");

					// Parse the response asynchronously.
					let key, option;
					const asyncParse = async () => {
						try {
							key = await this.onParseResponse(this.response);
							option = this.model.options[key];
							this.parsingError = false;
						} catch (e) {
							key = -1;
							option = undefined;
							this.parsingError = true;
							this.showableErrorMessage = this.determineErrorType(e);
						}
					};

					// Wait for both parsing and animation to finish.
					await Promise.all([
						asyncParse(),
						this.animationController.FadeIn.start({
							bubbleView: this.responseView,
						}).scroll(),
					]);

					// Log the submission
					const responseBubble = this.responseView;
					const response_fmt = String(this.response).toLowerCase().trim();
					const alreadySubmitted = this.submissions.indexOf(response_fmt) >= 0;
					this.submissions.push(response_fmt);

					// Handle the teacher response.
					if (alreadySubmitted && !(option && option.is_correct)) {
						if (this.taTrial) {
							this.taTrial.setFailed();
						}
						responseBubble.fail();
						await this.renderTeacherResponse(this.responseView.$el, true);
					} else if (option && option.trigger) {
						if (option.is_correct) {
							responseBubble.pass();
						} else {
							if (this.taTrial) {
								this.taTrial.setFailed();
							}
							responseBubble.fail();
						}
						// Traverse the new flow for this response.
						this.runnerController.createAndTraverseFlow({
							pointer: this.pointer.concat(["options", key]),
							context: this.context,
							item: option,
							flow: [{traverse: "trigger"}],
							onAdvance: ($newAnchor) => {
								if (option.is_correct) {
									this.passProblem($newAnchor);
								} else {
									if (this.taTrial) {
										this.taTrial.setFailed();
									}
									this.responseView = null;
									this.render($newAnchor);
								}
							},
							onBypass: () => {
								this.onBypass();
							},
							$anchor: responseBubble.$el,
						});
					} else if (option && option.is_correct) {
						// A correct option with no trigger.
						responseBubble.pass();
						const teacherBubbleView = this.layoutView.placeBubble(
							this.responseView.$el,
							"after",
							"teacher"
						);

						teacherBubbleView.setContent(
							ActionUtils.getRandomTeacherResponse("positive")
						);

						await this.animationController.FadeIn.start({
							bubbleView: teacherBubbleView,
						}).scroll();

						this.passProblem(teacherBubbleView.$el);
					} else if (this.model.options.length > 0) {
						// A wrong option with no trigger.
						if (this.taTrial) {
							this.taTrial.setFailed();
						}
						responseBubble.fail();
						await this.renderTeacherResponse(this.responseView.$el);
					} else {
						// There are no right/wrong options. So just pass it.
						responseBubble.pass();
						this.passProblem(this.responseView.$el);
					}
				},
			});
			this.submitButton = submitButton;
			if (this.submittable) {
				submitButton.$el.removeClass("off");
			} else {
				submitButton.$el.addClass("off");
			}
			await this.animationController.FadeIn.start({
				bubbleView: this.responseView,
			}).scroll();
			this.inputController.request({enableAnimations: true});
		} else {
			const $responseDiv = this.responseView.$el.find(".transporter-response");
			$responseDiv.css({"max-width": "320px"});
			await this.onRenderResponse($responseDiv, response, preview);
			this.mainView.scrollToTarget(this.responseView.$el);
			this.inputController.request({enableAnimations: true});
		}
	}

	/**
	 * Render's a default response from the teacher's side.
	 * Mutates `this.activeBubbleView`.
	 *
	 * @param {*} $anchor
	 */
	async renderTeacherResponse($anchor, alreadySubmitted = false) {
		var teacherBubbleView = this.layoutView.placeBubble(
			$anchor,
			"after",
			"teacher"
		);

		if (this.parsingError) {
			var defaultErrorMessage =
				"Whoops, something went wrong. Please make sure you're connected to the internet and try submitting that again.";
			var errorMessage = this.showableErrorMessage
				? this.showableErrorMessage
				: defaultErrorMessage;

			teacherBubbleView.setContent(errorMessage);
		} else {
			teacherBubbleView.setContent(
				ActionUtils.getRandomTeacherResponse(
					alreadySubmitted ? "repeat" : "negative"
				)
			);
		}

		await this.animationController.FadeIn.start({
			bubbleView: teacherBubbleView,
		}).scroll();
		this.responseView = null;
		this.render(teacherBubbleView.$el);
	}

	/**
	 * Returns true if the response should be considered empty.
	 * E.g. an array of empty strings
	 *
	 * Add new constraints to this function as necessary.
	 * @param {*} response
	 * @returns boolean
	 */
	isResponseEmpty(response) {
		if (_.isArray(response)) {
			if (response.join("") === "") {
				return true;
			}
		}
		if (_.isNaN(response)) {
			return true;
		}
	}

	/**
	 * Sets the current Submit button on/off.
	 *
	 * @param {boolean} val
	 */
	setSubmittable(val) {
		this.submittable = !!val;
		if (this.submitButton) {
			if (this.submittable) {
				this.submitButton.$el.removeClass("off");
			} else {
				this.submitButton.$el.addClass("off");
			}
		}
	}

	/**
	 * Use this function to allowlist certain error types that
	 * can be shown to the user.
	 *
	 * @param {object} error
	 */
	determineErrorType(error) {
		var errorAllowlist = ["E_PARSER_ERROR"];

		return errorAllowlist.includes(error.name) ? error.message : null;
	}
}
